import { Signal, isSignal, signal } from '@angular/core';
import { InjectionToken } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { isObservable, Observable } from 'rxjs';

/**
 * A single sibling navigation entry (e.g. prev/next within a detail
 * context, or peers of the active node).
 */
export interface SiblingNavEntry {
  label: string;
  url: string;
  /** True when this entry is the currently-active sibling. */
  current?: boolean;
}

/**
 * The shapes a {@link SiblingProvider.siblings} call may return — a
 * plain array (sync), or an async source (Signal / Observable / Promise).
 */
export type SiblingsInput =
  | SiblingNavEntry[]
  | Signal<SiblingNavEntry[]>
  | Observable<SiblingNavEntry[]>
  | Promise<SiblingNavEntry[]>;

/**
 * Source of "sibling" navigation entries for a context. May resolve
 * synchronously (array), or asynchronously via a Signal, Observable
 * or Promise — surfaces normalise whichever shape is provided.
 */
export interface SiblingProvider {
  /**
   * Optional owning navigation node id. When set, surfaces bind this
   * provider's siblings deterministically to the crumb/segment whose
   * resolved `navId` matches — rather than guessing by position. When
   * omitted, surfaces fall back to their default (positional) binding.
   */
  navId?: string;
  siblings(): SiblingsInput;
}

/**
 * Multi-provider token for {@link SiblingProvider} implementations.
 */
export const SIBLING_PROVIDER = new InjectionToken<SiblingProvider>('SIBLING_PROVIDER');

/**
 * Collapse any {@link SiblingsInput} shape into a single read-only
 * `Signal<SiblingNavEntry[]>` so surfaces can consume one shape:
 *
 *  - `Signal`     → returned as-is (already reactive).
 *  - `Observable` → wrapped via `toSignal` (initial value `[]`).
 *  - `Promise`    → resolved into a writable signal (seed `[]`).
 *  - `Array`      → wrapped in a constant signal.
 *
 * Must be called within an injection context when the input is an
 * Observable (so `toSignal` can register its cleanup) — surfaces invoke
 * it from a field initialiser / constructor, which satisfies that.
 */
export function normalizeSiblings(input: SiblingsInput): Signal<SiblingNavEntry[]> {
  if (isSignal(input)) {
    return input as Signal<SiblingNavEntry[]>;
  }
  if (isObservable(input)) {
    return toSignal(input, { initialValue: [] as SiblingNavEntry[] });
  }
  if (input instanceof Promise) {
    const out = signal<SiblingNavEntry[]>([]);
    void input.then((entries) => out.set(entries ?? []));
    return out.asReadonly();
  }
  // Plain array (sync).
  return signal<SiblingNavEntry[]>(input ?? []).asReadonly();
}
